import { useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle, Fragment } from 'react'
import { getTeamLogoUrl, getTeamLogoFallbackUrl } from '../../lib/teamLogos'
import { roundSeriesConfig } from '../../lib/bracketSeries'

function TeamLogo({ team, sportKey, size }) {
  const url = getTeamLogoUrl(team, sportKey)
  const [src, setSrc] = useState(url)
  const [hidden, setHidden] = useState(false)
  useEffect(() => { setSrc(getTeamLogoUrl(team, sportKey)); setHidden(false) }, [team, sportKey])
  if (!src || hidden) return null
  const px = size === 'xl' ? 'w-7 h-7' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'
  return <img src={src} alt="" className={`${px} object-contain shrink-0`} onError={() => {
    const fallback = getTeamLogoFallbackUrl(team, sportKey)
    if (fallback && fallback !== src) setSrc(fallback)
    else setHidden(true)
  }} />
}

// Split "Colorado Avalanche" → { city: "Colorado", name: "Avalanche" }
// Handles multi-word cities like "Los Angeles", "New York", "San Jose", etc.
const MULTI_WORD_CITIES = new Set([
  'Los Angeles', 'New York', 'New Jersey', 'San Jose', 'San Antonio', 'San Francisco',
  'San Diego', 'St. Louis', 'St Louis', 'Las Vegas', 'New Orleans', 'Oklahoma City',
  'Kansas City', 'Salt Lake', 'Green Bay', 'Tampa Bay', 'Golden State',
])

function splitTeamName(fullName) {
  if (!fullName) return { city: '', name: fullName || 'TBD' }
  const words = fullName.split(' ')
  if (words.length <= 1) return { city: '', name: fullName }
  // Try 3-word city, then 2-word city
  for (const len of [3, 2]) {
    if (words.length > len) {
      const candidate = words.slice(0, len).join(' ')
      if (MULTI_WORD_CITIES.has(candidate)) {
        return { city: candidate, name: words.slice(len).join(' ') }
      }
    }
  }
  // Default: first word is city, rest is team name
  return { city: words[0], name: words.slice(1).join(' ') }
}

function TeamRow({ team, seed, sportKey, size, className, cityClass, seriesRecord, recordPosition = 'top', mirrored }) {
  // Soccer (World Cup, MLS) uses country/club names that are unitary —
  // splitting "Bosnia and Herzegovina" into city/name renders "Bosnia"
  // grayed-out above "and Herzegovina" bold, which reads as wrong.
  // Skip the split and render the full name as a single bold line.
  const isSoccer = sportKey?.startsWith('soccer_')
  const { city, name } = isSoccer ? { city: '', name: team || 'TBD' } : splitTeamName(team)
  const padding = size === 'xl' ? 'px-3 py-2.5' : size === 'lg' ? 'px-2.5 py-2' : 'px-2 py-1.5'

  const logoEl = <TeamLogo team={team} sportKey={sportKey} size={size} />
  const seedEl = seed != null && (
    <span className={`text-text-muted ${size === 'xl' ? 'w-5' : 'w-4'} ${mirrored ? 'text-left' : 'text-right'} shrink-0`}>{seed}</span>
  )
  // Long names (>13 chars) get a smaller font so they fit on standard
  // bracket cards without truncating — covers "Bosnia and Herzegovina",
  // "Trinidad and Tobago", "United Arab Emirates", etc.
  const isLongName = (name?.length || 0) > 13
  const nameSize = size === 'xl'
    ? (isLongName ? 'text-sm' : 'text-base')
    : (isLongName ? 'text-[11px]' : 'text-sm')
  const nameEl = team ? (
    <div className={`flex flex-col min-w-0 flex-1 leading-tight ${mirrored ? 'items-end' : ''}`}>
      {city && (
        <span className={`truncate ${size === 'xl' ? 'text-xs' : 'text-[11px]'} ${cityClass || 'text-text-muted'}`}>{city}</span>
      )}
      <span className={`truncate font-semibold ${nameSize}`}>{name}</span>
    </div>
  ) : (
    <span className={`truncate flex-1 ${mirrored ? 'text-right' : ''}`}>TBD</span>
  )

  return (
    <div className={`relative flex items-center gap-1.5 ${padding} ${className}`}>
      {seriesRecord && (
        <span className={`absolute ${recordPosition === 'bottom' ? 'bottom-0.5' : 'top-0.5'} ${mirrored ? 'left-1.5' : 'right-1.5'} text-[9px] font-semibold text-text-muted z-10`}>{seriesRecord}</span>
      )}
      {mirrored ? (
        <>
          {nameEl}
          {seedEl}
          {logoEl}
        </>
      ) : (
        <>
          {logoEl}
          {seedEl}
          {nameEl}
        </>
      )}
    </div>
  )
}

function MatchupCard({ matchup, pick, pickData, eliminated, eliminatedTeams, showPick, onTap, alwaysTappable = false, awardsOneOff = true, size = 'default', playInPickResults = {}, isBestOf7 = false, sportKey, mirrored = false }) {
  const topCorrect = pick && matchup.status === 'completed' && pick === matchup.team_top && matchup.winner === 'top'
  const bottomCorrect = pick && matchup.status === 'completed' && pick === matchup.team_bottom && matchup.winner === 'bottom'
  const topWrong = pick && matchup.status === 'completed' && pick === matchup.team_top && matchup.winner === 'bottom'
  const bottomWrong = pick && matchup.status === 'completed' && pick === matchup.team_bottom && matchup.winner === 'top'

  const hasSeriesRecord = isBestOf7 && matchup.status === 'completed' && matchup.series_wins_top != null && matchup.series_wins_bottom != null
  // Live series: series has started (at least 1 win) but not yet completed
  const hasLiveSeries = isBestOf7 && matchup.status !== 'completed' && (matchup.series_wins_top > 0 || matchup.series_wins_bottom > 0)
  // Single-game matchups: tap once completed. The modal pulls the actual
  // score from the games table via useSeriesGames, so we don't gate on
  // matchup.score_top — some paths (admin populate, edge-case auto-settle)
  // leave that field null even though the game itself has scores.
  const hasCompletedScore = !isBestOf7 && matchup.status === 'completed'
  // Default: a tap only does something when there is a result to show, since
  // BracketView's handler opens game detail. The PICKER passes a navigation
  // handler instead ("jump to that step"), which must work before a single
  // game is played — otherwise its own "Tap any matchup to jump to that step"
  // hint sits above a grid where every card is inert.
  const canTap = !!onTap && (alwaysTappable || hasLiveSeries || hasSeriesRecord || hasCompletedScore)

  // Series length prediction color: white during series, green/yellow/red
  // after completion ONLY if the team pick was correct (no series-length
  // bonus points are awarded otherwise — coloring it would be misleading).
  let predictionColor = 'text-text-primary'
  if (hasSeriesRecord && pickData?.series_length) {
    if (topCorrect || bottomCorrect) {
      const actualLength = (matchup.series_wins_top || 0) + (matchup.series_wins_bottom || 0)
      const diff = Math.abs(pickData.series_length - actualLength)
      if (diff === 0) predictionColor = 'text-correct'
      // Yellow reads as partial credit, so it may only appear where the round
      // actually pays a one-off bonus. A best-of-3 pays none — with two
      // possible answers, "one game off" IS the other answer — so on a WNBA or
      // MLB opening round a miss is a miss and must render red, not yellow.
      else if (diff === 1 && awardsOneOff) predictionColor = 'text-yellow-500'
      else predictionColor = 'text-incorrect'
    } else {
      predictionColor = 'text-text-muted'
    }
  }

  function handleClick() {
    if (canTap) return onTap(matchup)
  }

  const isClickable = canTap

  function teamClass(team, isTop) {
    if (!team) return 'text-text-muted'
    if (showPick && pick === team) {
      // If pick was eliminated in a previous round, always show gray strikethrough
      if (eliminated) return 'text-text-muted line-through'
      if (isTop ? topCorrect : bottomCorrect) return 'text-correct font-semibold'
      if (isTop ? topWrong : bottomWrong) return 'text-incorrect line-through'
      return 'text-accent font-semibold'
    }
    // Show resolved teams that are eliminated (picked in feeder but lost)
    if (showPick && eliminatedTeams?.has(team)) return 'text-text-muted line-through'
    // Show play-in pick result on Round 1 teams only
    if (showPick && matchup.round_number <= 1 && playInPickResults[team] === 'correct') return 'text-correct font-semibold'
    if (showPick && matchup.round_number <= 1 && playInPickResults[team] === 'incorrect') return 'text-incorrect line-through'
    if (matchup.status === 'completed') {
      const isWinner = (isTop && matchup.winner === 'top') || (!isTop && matchup.winner === 'bottom')
      return isWinner ? 'font-semibold text-text-primary' : 'text-text-muted'
    }
    return 'text-text-primary'
  }

  // City color follows the row's status (green/red/strikethrough/muted)
  function cityClassFor(team, isTop) {
    if (!team) return 'text-text-muted'
    if (showPick && pick === team) {
      if (eliminated) return 'text-text-muted line-through'
      if (isTop ? topCorrect : bottomCorrect) return 'text-correct'
      if (isTop ? topWrong : bottomWrong) return 'text-incorrect line-through'
      return 'text-text-muted'
    }
    if (showPick && eliminatedTeams?.has(team)) return 'text-text-muted line-through'
    if (showPick && matchup.round_number <= 1 && playInPickResults[team] === 'correct') return 'text-correct'
    if (showPick && matchup.round_number <= 1 && playInPickResults[team] === 'incorrect') return 'text-incorrect line-through'
    return 'text-text-muted'
  }

  // Series prediction badge that sits over the divider line between teams.
  // User bracket: their predicted length, anchored to the team they picked.
  // Master bracket (no picks): actual series length on completed games, anchored to the winner.
  const predictionLength = isBestOf7 && showPick && pickData?.series_length && (pick === matchup.team_top || pick === matchup.team_bottom)
    ? pickData.series_length
    : null
  const actualSeriesLength = hasSeriesRecord
    ? (matchup.series_wins_top || 0) + (matchup.series_wins_bottom || 0)
    : null
  const masterLength = !showPick && actualSeriesLength ? actualSeriesLength : null
  const badgeLength = predictionLength || masterLength
  const badgeOnTopRow = predictionLength
    ? pick === matchup.team_top
    : matchup.winner === 'top'

  return (
    <div
      className={`relative bg-bg-primary/80 backdrop-blur-sm border border-text-primary/20 rounded-lg ${size === 'xl' ? 'w-52 text-sm' : size === 'lg' ? 'w-44 text-xs' : 'w-40 text-xs'} overflow-hidden${isClickable ? ' cursor-pointer hover:border-accent/50 transition-colors' : ''}`}
      onClick={isClickable ? handleClick : undefined}
    >
      <TeamRow
        team={matchup.team_top}
        seed={sportKey === 'soccer_world_cup' ? null : matchup.seed_top}
        sportKey={sportKey}
        size={size}
        mirrored={mirrored}
        className={`border-b border-text-primary/10 ${teamClass(matchup.team_top, true)}`}
        cityClass={cityClassFor(matchup.team_top, true)}
        seriesRecord={hasSeriesRecord && matchup.winner === 'top' ? `${matchup.series_wins_top}-${matchup.series_wins_bottom}` : null}
        recordPosition="top"
      />
      <TeamRow
        team={matchup.team_bottom}
        seed={sportKey === 'soccer_world_cup' ? null : matchup.seed_bottom}
        sportKey={sportKey}
        size={size}
        mirrored={mirrored}
        className={teamClass(matchup.team_bottom, false)}
        cityClass={cityClassFor(matchup.team_bottom, false)}
        seriesRecord={hasSeriesRecord && matchup.winner === 'bottom' ? `${matchup.series_wins_bottom}-${matchup.series_wins_top}` : null}
        recordPosition="bottom"
      />
      {badgeLength && (
        <div
          className={`absolute ${mirrored ? 'left-1.5' : 'right-1.5'} ${badgeOnTopRow ? 'top-1/2 -translate-y-full' : 'top-1/2'} z-20 pointer-events-none whitespace-nowrap leading-none`}
        >
          <span className={`text-[9px] font-semibold ${masterLength ? 'text-text-muted' : predictionColor}`}>in {badgeLength}</span>
        </div>
      )}
    </div>
  )
}

// Bracket geometry derived from the WIRING rather than assumed halving.
//
// Rows used to come from Math.pow(2, roundIdx) — "every round halves the
// field". True for 16 -> 8 -> 4 -> 2 and false for any bracket with byes:
// MLB's 4 wild card series feed 4 division series (each also seating a bye
// seed), the NFL's 6 wild cards feed 4 divisionals, the CFP's 4 first-round
// games feed 4 quarterfinals. With halving assumed, two wild cards were drawn
// into the SAME division series and later cards were placed on grid rows that
// do not exist.
//
// A first-round matchup occupies one row; any later matchup spans exactly the
// rows of the matchups feeding it. For a power-of-two bracket this reproduces
// the previous rows exactly, so nothing else changes.
//
// Shared by BOTH layouts. The linear one is used for single-region brackets;
// the FACING one is what MLB actually renders, since it has two regions — so
// fixing only the linear branch changed nothing on screen.
function computeWiringRows(byRound, roundList, templateMatchups) {
  const rows = new Map()
  const feeders = new Map()
  const keyOf = (m) => m.id ?? `${m.round_number}-${m.position}`

  // A LEAGUE renders bracket_matchups rows, which carry only
  // template_matchup_id — no feeds_into_* columns at all. The wiring lives on
  // bracket_template_matchups, passed in as templateMatchups. Without this
  // the league view fell straight through to the positional fallback, i.e.
  // back to assuming the field halves, which is the whole bug. The admin
  // builder is unaffected: its in-progress matchups carry feeds_into_round /
  // feeds_into_position and no templateMatchups prop.
  const feederTmIdsByTarget = new Map()
  for (const f of templateMatchups || []) {
    if (!f.feeds_into_matchup_id) continue
    if (!feederTmIdsByTarget.has(f.feeds_into_matchup_id)) feederTmIdsByTarget.set(f.feeds_into_matchup_id, new Set())
    feederTmIdsByTarget.get(f.feeds_into_matchup_id).add(f.id)
  }

  for (let ri = 0; ri < roundList.length; ri++) {
    const list = byRound[roundList[ri]] || []
    const prev = ri > 0 ? (byRound[roundList[ri - 1]] || []) : []

    list.forEach((m, idx) => {
      if (ri === 0) {
        rows.set(keyOf(m), { start: idx + 1, span: 1 })
        feeders.set(keyOf(m), [])
        return
      }
      // Explicit wiring, in the three shapes it reaches this component:
      //  1. league view  — tournament rows matched through the template
      //  2. template rows themselves — feeds_into_matchup_id on the row
      //  3. admin builder — feeds_into_round + feeds_into_position, no ids yet
      let mine = []
      const wantedTmIds = m.template_matchup_id ? feederTmIdsByTarget.get(m.template_matchup_id) : null
      if (wantedTmIds) mine = prev.filter((f) => f.template_matchup_id && wantedTmIds.has(f.template_matchup_id))
      if (!mine.length && m.id && feederTmIdsByTarget.get(m.id)) {
        const ids = feederTmIdsByTarget.get(m.id)
        mine = prev.filter((f) => ids.has(f.id))
      }
      if (!mine.length) {
        mine = prev.filter((f) =>
          (f.feeds_into_matchup_id && m.template_matchup_id && f.feeds_into_matchup_id === m.template_matchup_id) ||
          (f.feeds_into_round === m.round_number && f.feeds_into_position === m.position),
        )
      }
      if (!mine.length) mine = [prev[idx * 2], prev[idx * 2 + 1]].filter(Boolean)

      feeders.set(keyOf(m), mine)
      const blocks = mine.map((f) => rows.get(keyOf(f))).filter(Boolean)
      if (blocks.length) {
        const start = Math.min(...blocks.map((b) => b.start))
        const end = Math.max(...blocks.map((b) => b.start + b.span - 1))
        rows.set(keyOf(m), { start, span: end - start + 1 })
      } else {
        rows.set(keyOf(m), { start: idx + 1, span: 1 })
      }
    })
  }
  return { rows, feeders, keyOf }
}

export default forwardRef(function BracketDisplay({ matchups, picks, rounds, regions, onMatchupTap, initialRegion, seriesFormat, sportKey, templateMatchups, backdrop, alwaysTappable = false, containerized = false }, ref) {
  const isBestOf7 = seriesFormat === 'best_of_7'
  const [selectedRegion, setSelectedRegion] = useState(initialRegion ?? null)

  // Build pick lookup by template_matchup_id
  const pickMap = useMemo(() => {
    const map = {}
    for (const p of picks || []) {
      map[p.template_matchup_id] = { team: p.picked_team, eliminated: p.is_eliminated, points_earned: p.points_earned, is_correct: p.is_correct, series_length: p.series_length }
    }
    return map
  }, [picks])

  // Group matchups by round (exclude play-in round 0 from bracket view)
  const byRound = useMemo(() => {
    const grouped = {}
    for (const m of matchups || []) {
      if (m.round_number === 0) continue
      if (!grouped[m.round_number]) grouped[m.round_number] = []
      grouped[m.round_number].push(m)
    }
    // Sort each round by position
    for (const key in grouped) {
      grouped[key].sort((a, b) => a.position - b.position)
    }
    return grouped
  }, [matchups])

  // Filter by region when selected
  const filteredByRound = useMemo(() => {
    if (!selectedRegion) return byRound
    const filtered = {}
    for (const key in byRound) {
      const regionMatchups = byRound[key].filter((m) => m.region === selectedRegion)
      if (regionMatchups.length > 0) filtered[key] = regionMatchups
    }
    return filtered
  }, [byRound, selectedRegion])

  // Build team→seed map from Round 1 matchups (seeds only set on early rounds)
  const teamSeedMap = useMemo(() => {
    // EVERY round, earliest first — not round 1 only.
    //
    // A team with a first-round bye never appears in round 1: MLB's 1 and 2
    // seeds enter in the Division Series, the NFL's 1 seed in the Divisional
    // round, the CFP's top four in the quarterfinals. Restricting this to
    // round 1 left them with no seed at all, so the Guardians rendered as
    // "Cleveland Guardians" with a blank where "2" belongs while every team
    // that played a wild card kept its number.
    //
    // Worse than cosmetic for the NFL: reseeding sorts survivors by seed, and
    // the bye team is exactly the one whose seed decides the pairings.
    //
    // Ascending round order with first-write-wins keeps round 1 authoritative
    // for anyone who plays in it, so resolved later-round seeds can't
    // overwrite a real one.
    const map = {}
    const ordered = [...(matchups || [])].sort((a, b) => a.round_number - b.round_number)
    for (const m of ordered) {
      if (m.team_top && m.seed_top != null && map[m.team_top] == null) map[m.team_top] = m.seed_top
      if (m.team_bottom && m.seed_bottom != null && map[m.team_bottom] == null) map[m.team_bottom] = m.seed_bottom
    }
    return map
  }, [matchups])

  // Build feeder map for resolving team names from picks
  const feederMap = useMemo(() => {
    const map = {}
    const all = (matchups || []).filter((m) => m.round_number > 0)
    const byRoundLocal = {}
    for (const m of all) {
      if (!byRoundLocal[m.round_number]) byRoundLocal[m.round_number] = []
      byRoundLocal[m.round_number].push(m)
    }
    for (const key in byRoundLocal) {
      byRoundLocal[key].sort((a, b) => a.position - b.position)
    }

    // Build region order index for sorting cross-region feeders
    const regionOrder = {}
    if (regions) {
      for (let i = 0; i < regions.length; i++) regionOrder[regions[i]] = i
    }

    // Explicit wiring first. This was positional-only, which assumes every
    // round-N matchup is fed by exactly TWO consecutive round-(N-1)
    // matchups — a halving bracket. That is false the moment byes exist:
    // in MLB each Division Series matchup is fed by ONE Wild Card series
    // plus a bye seed, so matchup 0 drew lines from both Wild Card games
    // and matchup 1 looked for prevMatchups[2] and [3], which don't exist,
    // and drew nothing. Same for the NFL and the College Football Playoff.
    //
    // feeds_into_matchup_id says exactly where a winner goes. It lives on
    // bracket_template_matchups, not on the tournament rows, which is why it
    // has to be passed in — BracketPicker already resolves its own picks
    // this way, so the diagram was the only surface still guessing.
    const tmById = {}
    for (const tm of templateMatchups || []) tmById[tm.id] = tm
    const matchupByTemplateId = {}
    for (const m of all) if (m.template_matchup_id) matchupByTemplateId[m.template_matchup_id] = m

    for (const m of all) {
      if (m.round_number <= 1) continue

      const tm = m.template_matchup_id ? tmById[m.template_matchup_id] : null
      if (tm) {
        const feeders = (templateMatchups || []).filter((f) => f.feeds_into_matchup_id === tm.id)
        const topFeeder = feeders.find((f) => f.feeds_into_slot === 'top')
        const bottomFeeder = feeders.find((f) => f.feeds_into_slot === 'bottom')
        if (topFeeder || bottomFeeder) {
          // A slot with no feeder is a BYE — the seed is already seated, so
          // null is correct and means "draw no line into this side".
          map[m.id] = {
            top: topFeeder ? matchupByTemplateId[topFeeder.id] || null : null,
            bottom: bottomFeeder ? matchupByTemplateId[bottomFeeder.id] || null : null,
          }
          continue
        }
      }

      // Positional fallback — correct for every power-of-two bracket, and
      // what the admin builder still uses since its in-progress matchups
      // carry feeds_into_round/position rather than ids.
      const prevRound = byRoundLocal[m.round_number - 1]
      if (!prevRound?.length) continue
      let prevMatchups
      if (m.region) {
        prevMatchups = prevRound.filter((p) => p.region === m.region)
      } else {
        // Cross-region: sort by regions array order so FF pairs match left/right sides
        prevMatchups = [...prevRound].sort((a, b) => (regionOrder[a.region] ?? 99) - (regionOrder[b.region] ?? 99))
      }
      const myRound = byRoundLocal[m.round_number]
      const sameGroup = m.region ? myRound.filter((p) => p.region === m.region) : myRound
      const myIdx = sameGroup.indexOf(m)
      map[m.id] = { top: prevMatchups[myIdx * 2] || null, bottom: prevMatchups[myIdx * 2 + 1] || null }
    }
    return map
  }, [matchups, regions, templateMatchups])

  // Resolve team names from picks for matchups with null teams
  const resolvedMatchups = useMemo(() => {
    if (!picks?.length) return null

    function resolveFromFeeder(feeder) {
      if (!feeder) return null
      const pick = pickMap[feeder.template_matchup_id]?.team
      return pick || null
    }

    const resolved = {}

    // Resolve play-in picks into Round 1 null slots
    // Match by region + seed: a play-in between two 16-seeds feeds the 1-vs-16 R1 slot
    const playIns = (matchups || []).filter((m) => m.round_number === 0)
    if (playIns.length) {
      const r1WithNull = (matchups || []).filter((m) => m.round_number === 1 && (!m.team_top || !m.team_bottom))

      for (const r1 of r1WithNull) {
        // Determine the expected seed for the null slot
        const nullIsTop = !r1.team_top
        const expectedSeed = nullIsTop ? r1.seed_top : r1.seed_bottom

        // Find the play-in in the same region whose teams have the matching seed
        const matchingPlayIn = playIns.find((p) =>
          p.region === r1.region &&
          expectedSeed != null &&
          (p.seed_top === expectedSeed || p.seed_bottom === expectedSeed)
        )

        if (!matchingPlayIn) continue
        const resolvedTeam = resolveFromFeeder(matchingPlayIn)
        if (!resolvedTeam) continue

        resolved[r1.id] = {
          team_top: nullIsTop ? resolvedTeam : r1.team_top,
          team_bottom: nullIsTop ? r1.team_bottom : resolvedTeam,
          seed_top: nullIsTop ? (expectedSeed ?? teamSeedMap[resolvedTeam] ?? null) : r1.seed_top,
          seed_bottom: nullIsTop ? r1.seed_bottom : (expectedSeed ?? teamSeedMap[resolvedTeam] ?? null),
        }
      }
    }

    // Resolve later rounds (Round 2+) from picks
    // Always show the user's picked teams, not the actual advanced teams
    for (const m of matchups || []) {
      if (m.round_number <= 1) continue
      const feeders = feederMap[m.id]
      if (!feeders) continue
      const topResolved = resolveFromFeeder(feeders.top)
      const bottomResolved = resolveFromFeeder(feeders.bottom)
      // When viewing picks, don't fall back to actual matchup teams — show TBD
      // for unpicked slots.
      //
      // EXCEPT a slot with no feeder at all, which is a BYE: the team is
      // seated on the matchup itself and no pick will ever fill it. Treating
      // it like an unpicked slot blanked the highest seeds out of their own
      // bracket — a completed MLB entry read "TBD / New York Yankees" for the
      // Division Series when the top line should say Tampa Bay Rays.
      const topIsBye = !feeders.top
      const bottomIsBye = !feeders.bottom
      const topTeam = topResolved || (topIsBye || !picks?.length ? m.team_top : null)
      const bottomTeam = bottomResolved || (bottomIsBye || !picks?.length ? m.team_bottom : null)
      if (topTeam !== m.team_top || bottomTeam !== m.team_bottom) {
        resolved[m.id] = {
          team_top: topTeam,
          team_bottom: bottomTeam,
          seed_top: topTeam ? (teamSeedMap[topTeam] ?? m.seed_top ?? null) : null,
          seed_bottom: bottomTeam ? (teamSeedMap[bottomTeam] ?? m.seed_bottom ?? null) : null,
        }
      }
    }
    return resolved
  }, [matchups, picks, pickMap, feederMap, teamSeedMap])

  // Determine facing bracket layout (2+ regions, no specific region selected)
  const facingLayout = useMemo(() => {
    if (selectedRegion || !regions || regions.length < 2) return null

    // 2 regions: first region left, second region right (matches admin ordering)
    // 4 regions: first 2 left, last 2 right (NCAA-style)
    const left = regions.length === 2 ? [regions[0]] : [regions[0], regions[1]]
    const right = regions.length === 2 ? [regions[1]] : [regions[2], regions[3]]

    // Determine which rounds have regional matchups vs cross-region
    const regionalRoundSet = new Set()
    const crossRoundSet = new Set()
    for (const m of matchups || []) {
      if (m.round_number > 0) {
        if (m.region) regionalRoundSet.add(m.round_number)
        else crossRoundSet.add(m.round_number)
      }
    }

    return {
      left,
      right,
      regionalRounds: [...regionalRoundSet].sort((a, b) => a - b),
      crossRounds: [...crossRoundSet].sort((a, b) => a - b),
    }
  }, [selectedRegion, regions, matchups])

  // Center column matchups (FF + Championship) for facing layout
  const centerMatchups = useMemo(() => {
    if (!facingLayout) return null

    const ffRound = facingLayout.crossRounds.find((r) => (byRound[r]?.length || 0) === 2)
    const champRound = facingLayout.crossRounds.find((r) => (byRound[r]?.length || 0) === 1)

    // 2-region brackets (NBA): no FF round, just championship in center
    if (!ffRound && champRound) {
      return {
        ffLeft: null,
        ffRight: null,
        championship: byRound[champRound]?.[0] || null,
        ffRound: null,
        champRound,
      }
    }

    if (!ffRound) return null

    const ffMatchups = [...(byRound[ffRound] || [])].sort((a, b) => a.position - b.position)

    // Determine which FF matchup goes on which side by checking which E8 regions feed into it
    // E8 matchups sorted by position pair into FF: [0,1] → FF[0], [2,3] → FF[1]
    const lastRegRound = facingLayout.regionalRounds[facingLayout.regionalRounds.length - 1]
    const e8 = [...(byRound[lastRegRound] || [])].sort((a, b) => a.position - b.position)
    const ff0Regions = [e8[0]?.region, e8[1]?.region].filter(Boolean)

    // Check if FF[0]'s feeder regions overlap with the left side
    const leftSet = new Set(facingLayout.left)
    const ff0IsLeft = ff0Regions.some((r) => leftSet.has(r))

    return {
      ffLeft: ff0IsLeft ? ffMatchups[0] : ffMatchups[1],
      ffRight: ff0IsLeft ? ffMatchups[1] : ffMatchups[0],
      championship: champRound ? byRound[champRound]?.[0] : null,
      ffRound,
      champRound,
    }
  }, [facingLayout, byRound])

  // Half R1 count for facing layout grids (number of R1 matchups per side)
  const halfR1Count = useMemo(() => {
    if (!facingLayout) return 0
    const r1 = facingLayout.regionalRounds[0]
    return (byRound[r1] || []).filter((m) => facingLayout.left.includes(m.region)).length
  }, [facingLayout, byRound])

  // Gap-aware grid helpers for facing layout
  // 2 regions per side (NCAA): spacer row between the two regions
  // 1 region per side (NBA): no spacer needed
  const regionsPerSide = facingLayout ? facingLayout.left.length : 1
  const perRegionCount = regionsPerSide > 1 ? halfR1Count / regionsPerSide : halfR1Count
  const facingGridTemplate = regionsPerSide > 1
    ? `repeat(${perRegionCount}, minmax(100px, 1fr)) 20px repeat(${perRegionCount}, minmax(100px, 1fr))`
    : `repeat(${halfR1Count}, minmax(100px, 1fr))`

  // Row for one card in a facing half, taken from the wiring. Falls back to
  // the index when a matchup has no block (shouldn't happen, but a missing
  // row must not collapse the whole column onto row 1).
  function facingGridRowFor(layout, matchup, idx) {
    const b = layout.rows.get(layout.keyOf(matchup))
    if (!b) return facingGridRow(idx, 1)
    // The multi-region gap row (4-region brackets) still applies.
    const start = regionsPerSide > 1 && b.start > perRegionCount ? b.start + 1 : b.start
    return `${start} / span ${b.span}`
  }

  function facingGridRow(idx, span) {
    if (regionsPerSide <= 1) {
      // No gap — simple grid row
      return `${idx * span + 1} / span ${span}`
    }
    const start = idx * span + 1
    return start > perRegionCount
      ? `${start + 1} / span ${span}`
      : `${start} / span ${span}`
  }

  const useFacing = !!(facingLayout && centerMatchups)

  const roundNumbers = Object.keys(filteredByRound).map(Number).sort((a, b) => a - b)
  const firstRoundCount = filteredByRound[roundNumbers[0]]?.length || 0

  // ── Linear-layout geometry, derived from the WIRING rather than assumed ──
  //
  // Card rows and connector shapes used to come from `Math.pow(2, roundIdx)`,
  // which encodes "every round halves the field". True for 16 -> 8 -> 4 -> 2,
  // false for any bracket with byes:
  //
  //   MLB   4 Wild Card series feed 4 Division Series (each DS also seats a
  //         bye seed), so 4 -> 4, not 4 -> 2.
  //   NFL   6 Wild Card games feed 4 Divisional games.
  //   CFP   4 first-round games feed 4 quarterfinals.
  //
  // With halving assumed, both AL wild cards were drawn into the SAME
  // Division Series and the 2-seed's series got a line from nowhere, while
  // later-round cards were placed on grid rows that do not exist.
  //
  // Instead: a first-round matchup occupies one row, and any later matchup
  // spans exactly the rows of the matchups that feed it. A Division Series
  // fed by one wild card sits beside that wild card; an LCS fed by two
  // Division Series spans both and centres between them. For a power-of-two
  // bracket this reproduces the old rows exactly, so nothing else changes.
  const linearLayout = useMemo(
    () => computeWiringRows(filteredByRound, roundNumbers, templateMatchups),
    [filteredByRound, roundNumbers, templateMatchups],
  )


  function getRoundName(roundNum) {
    const r = (rounds || []).find((r) => r.round_number === roundNum)
    return r?.name || `Round ${roundNum}`
  }

  function getRoundPoints(roundNum) {
    const r = (rounds || []).find((r) => r.round_number === roundNum)
    return r?.points_per_correct || 0
  }

  // Build play-in pick results: team name → 'correct' | 'incorrect'
  // So Round 1 matchup cards can show green/red for play-in winners
  const playInPickResults = useMemo(() => {
    const map = {}
    const playInMatchups = (matchups || []).filter((m) => m.round_number === 0)
    for (const m of playInMatchups) {
      const pick = pickMap[m.template_matchup_id]
      if (!pick || m.status !== 'completed' || !m.winner) continue
      const winningTeam = m.winner === 'top' ? m.team_top : m.team_bottom
      if (pick.team === winningTeam) {
        map[winningTeam] = 'correct'
      } else {
        map[pick.team] = 'incorrect'
        map[winningTeam] = 'unpicked'
      }
    }
    return map
  }, [matchups, pickMap])

  // Build set of eliminated team names from picks
  // Includes teams from incorrect picks (lost their game) and explicitly eliminated downstream picks
  const eliminatedTeams = useMemo(() => {
    const set = new Set()
    for (const p of picks || []) {
      if (p.is_eliminated || p.is_correct === false) set.add(p.picked_team)
    }
    return set
  }, [picks])

  const hasPicks = picks && picks.length > 0
  // Soccer World Cup halves have no FIFA-issued names — internal
  // placeholders ("Side 1" / "Side 2") drive the facing left/right
  // layout but are never shown to users. See memory:
  // feedback_world_cup_no_half_names.
  const isWorldCup = sportKey === 'soccer_world_cup'
  const showRegionTabs = !isWorldCup && regions && regions.length >= 2

  // ── Helper: render a matchup card with resolved data ──

  function renderCard(matchup, size, mirrored = false) {
    const resolved = resolvedMatchups?.[matchup.id]
    const dm = resolved
      ? {
          ...matchup,
          team_top: 'team_top' in resolved ? resolved.team_top : matchup.team_top,
          team_bottom: 'team_bottom' in resolved ? resolved.team_bottom : matchup.team_bottom,
          seed_top: 'seed_top' in resolved ? resolved.seed_top : matchup.seed_top,
          seed_bottom: 'seed_bottom' in resolved ? resolved.seed_bottom : matchup.seed_bottom,
        }
      : matchup

    return (
      <MatchupCard
        matchup={dm}
        pick={pickMap[matchup.template_matchup_id]?.team}
        pickData={pickMap[matchup.template_matchup_id]}
        eliminated={pickMap[matchup.template_matchup_id]?.eliminated}
        eliminatedTeams={eliminatedTeams}
        showPick={hasPicks}
        onTap={onMatchupTap}
                              alwaysTappable={alwaysTappable}
        size={size}
        playInPickResults={playInPickResults}
        isBestOf7={isBestOf7}
                              awardsOneOff={roundSeriesConfig(rounds, matchup.round_number, seriesFormat).oneOffBonus > 0}
        sportKey={sportKey}
        mirrored={mirrored}
      />
    )
  }

  // ── Helper: render one bracket connector element (merge 2→1) ──

  function renderConnectorElement(mirrored) {
    if (mirrored) {
      return (
        <>
          <div className="w-3 flex flex-col">
            <div className="flex-1 border-b border-border/70" />
            <div className="flex-1" />
          </div>
          <div className="w-3 flex flex-col">
            <div className="flex-1" />
            <div className="flex-1 border-t border-l border-border/70" />
            <div className="flex-1 border-b border-l border-border/70" />
            <div className="flex-1" />
          </div>
        </>
      )
    }
    return (
      <>
        <div className="w-3 flex flex-col">
          <div className="flex-1" />
          <div className="flex-1 border-t border-r border-border/70" />
          <div className="flex-1 border-b border-r border-border/70" />
          <div className="flex-1" />
        </div>
        <div className="w-3 flex flex-col">
          <div className="flex-1 border-b border-border/70" />
          <div className="flex-1" />
        </div>
      </>
    )
  }

  // ── Helper: render a column of connector elements ──

  // One connector per matchup BEING FED, placed on that matchup's own row
  // block. A matchup with two feeders gets the merge fork; one fed by a single
  // winner — the other side being a bye seed — gets a straight line, since
  // there is nothing to merge with and the fork drew an arm from a matchup
  // that does not feed it.
  function renderConnectorColumn(targets, layout, gridTemplate, mirrored, key) {
    return (
      <div key={key} className="flex flex-col">
        <div className="text-xs font-semibold mb-1 invisible">&nbsp;</div>
        <div className="text-[10px] mb-3 invisible">&nbsp;</div>
        <div className="grid gap-y-2" style={{ gridTemplateRows: gridTemplate }}>
          {targets.map((t, idx) => {
            const feedCount = (layout.feeders.get(layout.keyOf(t)) || []).length
            return (
              <div
                key={t.id ?? idx}
                className="flex"
                style={{ gridRow: facingGridRowFor(layout, t, idx) }}
              >
                {feedCount >= 2 ? renderConnectorElement(mirrored) : (
                  <div className="w-6 flex flex-col">
                    <div className="flex-1 border-b border-border/70" />
                    <div className="flex-1" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Facing bracket: render one half (left or right side) ──

  function renderBracketHalf(halfRegions, mirrored, side) {
    const { regionalRounds } = facingLayout

    // Matchups for this half, grouped by round
    const halfByRound = {}
    for (const r of regionalRounds) {
      halfByRound[r] = (byRound[r] || []).filter((m) => halfRegions.includes(m.region))
    }

    const displayRounds = mirrored ? [...regionalRounds].reverse() : regionalRounds
    const elements = []
    // Rows for THIS half, from the wiring. MLB renders here, not in the
    // linear branch, because it has two regions.
    const halfLayout = computeWiringRows(halfByRound, regionalRounds, templateMatchups)

    displayRounds.forEach((roundNum, displayIdx) => {
      const matchupsList = halfByRound[roundNum] || []
      const isLastDisplay = displayIdx === displayRounds.length - 1

      // Round column
      elements.push(
        <div key={`${side}-r-${roundNum}`} className="flex flex-col items-center">
          <table className="mb-3 mx-auto"><tbody>
            <tr><td className="text-center text-xs font-semibold text-text-secondary whitespace-nowrap">{getRoundName(roundNum)}</td></tr>
            <tr><td className="text-center text-xs text-text-muted whitespace-nowrap">{getRoundPoints(roundNum)} pts</td></tr>
          </tbody></table>
          <div
            className="grid gap-y-2"
            style={{ gridTemplateRows: facingGridTemplate }}
          >
            {matchupsList.map((matchup, idx) => (
              <div
                key={matchup.id}
                className="flex items-center"
                style={{ gridRow: facingGridRowFor(halfLayout, matchup, idx) }}
              >
                {renderCard(matchup, 'default', mirrored)}
              </div>
            ))}
          </div>
        </div>
      )

      // Inter-round connector
      if (!isLastDisplay) {
        // The connector column belongs to whichever round is closer to the
        // centre — the one being fed. Mirrored halves read right-to-left, so
        // that is the CURRENT round there and the NEXT round otherwise.
        const targets = mirrored
          ? matchupsList
          : (halfByRound[displayRounds[displayIdx + 1]] || [])

        if (targets.length > 0) {
          elements.push(
            renderConnectorColumn(targets, halfLayout, facingGridTemplate, mirrored, `${side}-c-${roundNum}`)
          )
        }
      }
    })

    // Merge-to-center connector (last regional round → center)
    // If the last regional round has only 1 matchup per side (e.g. semifinals
    // in a 2-conference bracket), draw a straight line instead of a fork.
    const lastRegRound = regionalRounds[regionalRounds.length - 1]
    const lastRoundCount = (halfByRound[lastRegRound] || []).length

    if (lastRoundCount <= 1) {
      // Straight horizontal line
      const lineConn = (
        <div key={`${side}-merge`} className="flex flex-col">
          <div className="text-xs font-semibold mb-1 invisible">&nbsp;</div>
          <div className="text-[10px] mb-3 invisible">&nbsp;</div>
          <div className="grid gap-y-2" style={{ gridTemplateRows: facingGridTemplate }}>
            <div style={{ gridRow: '1 / -1' }} className="flex items-center">
              <div className="w-6 h-px bg-border/70" />
            </div>
          </div>
        </div>
      )
      if (mirrored) {
        elements.unshift(lineConn)
      } else {
        elements.push(lineConn)
      }
    } else {
      // Merge to the centre spans the whole half, so it is its own shape
      // rather than a per-matchup connector.
      const mergeConn = (
        <div key={`${side}-merge`} className="flex flex-col">
          <div className="text-xs font-semibold mb-1 invisible">&nbsp;</div>
          <div className="text-[10px] mb-3 invisible">&nbsp;</div>
          <div className="grid gap-y-2" style={{ gridTemplateRows: facingGridTemplate }}>
            <div style={{ gridRow: '1 / -1' }} className="flex">
              {renderConnectorElement(mirrored)}
            </div>
          </div>
        </div>
      )
      if (mirrored) {
        elements.unshift(mergeConn)
      } else {
        elements.push(mergeConn)
      }
    }

    return elements
  }

  // ── Facing bracket: render a center column matchup (FF or Championship) ──

  function renderCenterMatchup(matchup, size, roundNum) {
    if (!matchup) return null
    return (
      <div className="flex flex-col items-center">
        <div className="text-xs font-semibold text-text-secondary mb-1">
          {getRoundName(roundNum)}
        </div>
        <div className="text-[10px] text-text-muted mb-3">
          {getRoundPoints(roundNum)} pts
        </div>
        <div
          className="grid gap-y-2"
          style={{ gridTemplateRows: facingGridTemplate }}
        >
          <div style={{ gridRow: '1 / -1' }} className="flex items-center">
            {renderCard(matchup, size)}
          </div>
        </div>
      </div>
    )
  }

  // ── Facing bracket: horizontal line connector between center cards ──

  function renderCenterLine() {
    return (
      <div className="flex flex-col">
        <div className="text-xs font-semibold mb-1 invisible">&nbsp;</div>
        <div className="text-[10px] mb-3 invisible">&nbsp;</div>
        <div
          className="grid gap-y-2"
          style={{ gridTemplateRows: facingGridTemplate }}
        >
          <div style={{ gridRow: '1 / -1' }} className="flex items-center">
            <div className="w-4 h-px bg-border/70" />
          </div>
        </div>
      </div>
    )
  }

  // The centerpiece image renders INSIDE the horizontally-scrolling bracket
  // content rather than in the page container around it. That is what lets it
  // show on phones: the bracket is wider than the viewport and scrolls, so an
  // image anchored to the container would sit still while the bracket slid out
  // from under it. Anchored here, x/y mean "position along the BRACKET" and
  // hold at every screen size — so x=80 puts it near the Finals on a linear
  // bracket, and x=50 still centers it on the facing layouts where the final
  // sits in the middle.
  //
  // z-index 11 preserves the paint order this image has always had: it was an
  // absolutely-positioned sibling ahead of the bracket, so it drew OVER the
  // cards at its own opacity. 11 clears the facing layout's z-10 content so
  // that stays true in both layouts. 'above_finals' keeps its higher 20.
  //
  // crossOrigin matters now that this node is inside the element Share Bracket
  // rasterizes — a cross-origin image without it taints the canvas and breaks
  // sharing. Supabase storage returns `access-control-allow-origin: *`.
  function renderBackdrop() {
    if (!backdrop?.url) return null
    return (
      <img
        src={backdrop.url}
        alt=""
        draggable={false}
        crossOrigin="anonymous"
        className="max-w-[80%] max-h-full"
        style={{
          position: 'absolute',
          left: `${backdrop.x ?? 50}%`,
          top: `${backdrop.y ?? 50}%`,
          transform: `translate(-50%, -50%) scale(${backdrop.scale ?? 1})`,
          opacity: backdrop.opacity ?? 0.4,
          zIndex: backdrop.position === 'above_finals' ? 20 : 11,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
    )
  }

  // Desktop horizontal view — break out of parent max-w container to use
  // full viewport width. When `containerized` is true (e.g. admin Step 5
  // preview inside a narrower panel), skip the viewport-escape so the
  // bracket fits its parent and scrolls horizontally if needed.
  return (
    <div
      className={containerized
        ? 'w-full overflow-x-auto'
        : `xl:w-[calc(100vw-3rem)] ${useFacing ? '' : 'xl:max-w-[1400px]'} xl:-ml-[calc((100vw-3rem-100%)/2)] xl:self-center`}
    >
      {/* Region tabs — glass-edge styling matched to the main league tabs */}
      {showRegionTabs && (
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          <button
            onClick={() => setSelectedRegion(null)}
            className={`relative shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors backdrop-blur-sm whitespace-nowrap ${
              !selectedRegion
                ? 'bg-bg-primary/20 text-accent border-2 border-accent'
                : 'bg-bg-primary/20 text-text-primary hover:bg-bg-primary/40 border border-text-primary/15'
            }`}
          >
            All
          </button>
          {regions.map((region) => (
            <button
              key={region}
              onClick={() => setSelectedRegion(region)}
              className={`relative shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors backdrop-blur-sm whitespace-nowrap ${
                selectedRegion === region
                  ? 'bg-bg-primary/20 text-accent border-2 border-accent'
                  : 'bg-bg-primary/20 text-text-primary hover:bg-bg-primary/40 border border-text-primary/15'
              }`}
            >
              {region}
            </button>
          ))}
        </div>
      )}

      <div ref={ref} className="overflow-x-auto">
        {useFacing ? (
          /* ── Facing bracket layout ── */
          <div className="relative flex min-w-max py-2">
            <div className="relative z-10 flex min-w-max w-full">
              {renderBracketHalf(facingLayout.left, false, 'left')}
              {centerMatchups.ffLeft && renderCenterMatchup(centerMatchups.ffLeft, 'lg', centerMatchups.ffRound)}
              {centerMatchups.championship && renderCenterLine()}
              {centerMatchups.championship &&
                renderCenterMatchup(centerMatchups.championship, 'xl', centerMatchups.champRound)}
              {centerMatchups.championship && renderCenterLine()}
              {centerMatchups.ffRight && renderCenterMatchup(centerMatchups.ffRight, 'lg', centerMatchups.ffRound)}
              {renderBracketHalf(facingLayout.right, true, 'right')}
            </div>
            {renderBackdrop()}
          </div>
        ) : (
          /* ── Linear layout (single region or small bracket) ──
             `relative` so the centerpiece anchors to the bracket's full
             scroll width, not the viewport. No offsets, so flow is unchanged. */
          <div className="relative flex min-w-max py-2">
            {roundNumbers.map((roundNum, roundIdx) => {
              const matchupsList = filteredByRound[roundNum] || []
              const isLast = roundIdx === roundNumbers.length - 1
              const cardSize =
                roundIdx === roundNumbers.length - 1
                  ? 'xl'
                  : roundIdx === roundNumbers.length - 2
                    ? 'lg'
                    : 'default'
              const nextMatchupCount = !isLast
                ? filteredByRound[roundNumbers[roundIdx + 1]]?.length || 0
                : 0

              return (
                <Fragment key={roundNum}>
                  <div className="flex flex-col items-center">
                    <div className="text-xs font-semibold text-text-secondary mb-1">
                      {getRoundName(roundNum)}
                    </div>
                    <div className="text-[10px] text-text-muted mb-3">
                      {getRoundPoints(roundNum)} pts
                    </div>
                    <div
                      className="grid gap-y-2"
                      style={{
                        gridTemplateRows: `repeat(${firstRoundCount}, minmax(100px, 1fr))`,
                      }}
                    >
                      {matchupsList.map((matchup, idx) => {
                        const resolved = resolvedMatchups?.[matchup.id]
                        const displayMatchup = resolved
                          ? {
                              ...matchup,
                              team_top: 'team_top' in resolved ? resolved.team_top : matchup.team_top,
                              team_bottom: 'team_bottom' in resolved ? resolved.team_bottom : matchup.team_bottom,
                              seed_top: 'seed_top' in resolved ? resolved.seed_top : matchup.seed_top,
                              seed_bottom: 'seed_bottom' in resolved ? resolved.seed_bottom : matchup.seed_bottom,
                            }
                          : matchup
                        return (
                          <div
                            key={matchup.id}
                            className="flex items-center"
                            style={(() => {
                              const b = linearLayout.rows.get(linearLayout.keyOf(matchup))
                              return { gridRow: b ? `${b.start} / span ${b.span}` : `${idx + 1} / span 1` }
                            })()}
                          >
                            <MatchupCard
                              matchup={displayMatchup}
                              pick={pickMap[matchup.template_matchup_id]?.team}
                              pickData={pickMap[matchup.template_matchup_id]}
                              eliminated={pickMap[matchup.template_matchup_id]?.eliminated}
                              eliminatedTeams={eliminatedTeams}
                              showPick={hasPicks}
                              onTap={onMatchupTap}
                              alwaysTappable={alwaysTappable}
                              size={cardSize}
                              playInPickResults={playInPickResults}
                              isBestOf7={isBestOf7}
                              awardsOneOff={roundSeriesConfig(rounds, matchup.round_number, seriesFormat).oneOffBonus > 0}
                              sportKey={sportKey}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  {/* Bracket connector lines to next round */}
                  {!isLast && nextMatchupCount > 0 && (
                    <div className="flex flex-col">
                      <div className="text-xs font-semibold mb-1 invisible">&nbsp;</div>
                      <div className="text-[10px] mb-3 invisible">&nbsp;</div>
                      <div
                        className="grid gap-y-2"
                        style={{
                          gridTemplateRows: `repeat(${firstRoundCount}, minmax(100px, 1fr))`,
                        }}
                      >
                        {(filteredByRound[roundNumbers[roundIdx + 1]] || []).map((nextM, idx) => {
                          const block = linearLayout.rows.get(linearLayout.keyOf(nextM))
                          const feedCount = (linearLayout.feeders.get(linearLayout.keyOf(nextM)) || []).length
                          return (
                            <div
                              key={idx}
                              className="flex"
                              style={{ gridRow: block ? `${block.start} / span ${block.span}` : `${idx + 1} / span 1` }}
                            >
                              {/* A matchup fed by TWO winners gets the merge glyph. One fed
                                  by a single winner — the other side being a bye seed — gets
                                  a straight line, because there is nothing to merge with and
                                  the old glyph drew an arm from a matchup that doesn't feed
                                  it. */}
                              {feedCount >= 2 ? (
                                <>
                                  <div className="w-3 flex flex-col">
                                    <div className="flex-1" />
                                    <div className="flex-1 border-t border-r border-border/70" />
                                    <div className="flex-1 border-b border-r border-border/70" />
                                    <div className="flex-1" />
                                  </div>
                                  <div className="w-3 flex flex-col">
                                    <div className="flex-1 border-b border-border/70" />
                                    <div className="flex-1" />
                                  </div>
                                </>
                              ) : (
                                <div className="w-6 flex flex-col">
                                  <div className="flex-1 border-b border-border/70" />
                                  <div className="flex-1" />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Fragment>
              )
            })}
            {renderBackdrop()}
          </div>
        )}

        {/* Legend */}
        {hasPicks && (
          <div className="flex gap-4 mt-4 ml-2 text-[10px] text-text-muted">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 bg-correct rounded-full" />
              <span>Correct</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 bg-red-700 rounded-full" />
              <span>Wrong</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 bg-accent rounded-full" />
              <span>Your Pick</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="line-through">X</span>
              <span>Eliminated</span>
            </span>
          </div>
        )}
      </div>
    </div>
  )
})
